const en = {
    common: {
        view: "View",
        backHome: "Back to Home",
        backToPanel: "Back to Panel",
        menu: "Menu",
        email: "Email",
        clear: "Clear",
        submit: "Submit",
        go: "Go",
        copyLink: "Copy Link",
        share: "Share",
        copyright: "©{year} by Hei Creative.",
        days: "{count} days",
        networkTroubleshootHint:
            "Please try clearing the cache, switching networks, or opening the link in your default browser.",
        scrollNav: "Quick scroll",
        scrollToTop: "Back to top",
        scrollToBottom: "Scroll to bottom",
        redTitle: "The Red (Xiaohongshu)",
        instagramTitle: "Instagram",
    },
    language: {
        label: "Language",
        zh: "简体",
        yue: "繁體",
        en: "English",
    },
    nav: {
        about: "About Us",
        portfolio: "Portfolio",
        photography: "Photography",
        creatorPartnerships: "Creator Partnerships",
        contact: "Contact Us",
        portrait: "Portrait",
        wedding: "Wedding",
        event: "Events",
        commercial: "Exhibitions",
        pets: "Pets",
        fashion: "Fashion",
        destination: "Destination",
        creative: "Creative",
        scenery: "Scenery",
    },
    home: {
        portrait: "Portrait",
        wedding: "Wedding",
        event: "Major Event",
        commercial: "Exhibitions",
        pets: "Pets",
        fashion: "Fashion",
        destination: "Destination Photoshoot",
        creative: "Creative",
        scenery: "Scenery",
    },
    pages: {
        about: {
            title: "About Us | 关于我们",
            intro: "We are a comprehensive media company focused on **photography, film production, and creator partnerships**. Based in the UK, we combine professional visual expertise with a strong creator network to deliver diverse visual content and communication services for individuals, institutions, and brands.",
            focusAreas: [
                { label: "**Photography & Film Production**", text: "Covering commercial events, brand content, portraits, weddings and bridal shoots, travel and lifestyle photography, and fashion visuals." },
                { label: "**Creator Partnerships**", text: "Connecting leading creators across major platforms with brands for commercial campaigns, content co-creation, and offline event collaborations." },
            ],
            photography: {
                title: "Photography Service",
                moreLink: "Learn more",
                lead: "Our team brings industry-leading photography and production experience in the UK, with a strong track record covering large-scale events. Core team members have:",
                highlights: [
                    "Served as **lead photographer for the 2026 UK Chinese New Year Gala** and were selected as the **only mainland China photographer on the official photography team for the 2026 London Marathon (a World Athletics Platinum Label race)**;",
                    "Photographed news coverage for international figures including the **Chinese Ambassador to the UK**;",
                    "Played a key role in visual coverage of major events including the **UK concert commemorating the 80th anniversary of victory in the War of Resistance, London Fashion Week, the Harvard China Innovation and Investment Forum, and the Ministry of Culture and Tourism's Chinese New Year Garden Party in London**;",
                    "Published and featured repeatedly in authoritative central media including **China Daily and China News Service**.",
                ],
                summary: "With a professional production team and extensive live-event experience, we provide photography services across **conferences, exhibitions, large-scale events, weddings, bridal shoots, portraits, travel shoots, birthdays, family sessions, and fashion**.",
            },
            partnerships: {
                title: "Creator Partnerships",
                moreLink: "Learn more",
                lead: "We currently work with **a broad range of influential Key Opinion Leaders and content creators based in the UK**, spanning different content verticals and audience communities, and have completed brand content collaborations across multiple brands and categories.",
                body: "Backed by our creator network and partnership experience, we help brands connect with the right content partners to build authentic, influential brand storytelling in both the UK and China markets.",
                listTitle: "Past collaborations include:",
                items: [
                    "**Brand offline events** | Inviting Key Opinion Leaders to product launches, promotional events, and large-scale offline events to boost on-site engagement and social reach;",
                    "**Event hosting and content teams** | Matching Key Opinion Leader hosts and creator teams to event needs, balancing live performance with online distribution;",
                    "**Product promotion and brand endorsement** | Matching suitable leading creators to brand positioning, raising brand awareness through product experiences, content sharing, and social media distribution;",
                    "**Concerts and cultural entertainment** | Planning creator participation and content distribution around concerts, cultural events, and other scenarios;",
                    "**Street interactions and challenge content** | Creating authentic, natural, and highly shareable social media content through street interviews, interactive challenges, and similar formats.",
                ],
            },
            philosophy: {
                title: "Our Philosophy",
                body: "We believe that **every individual who steps in front of the camera has a unique story worth being seen; \n every brand going global deserves a broader voice and the opportunity to be seen.**",
                closing: "From documenting important moments to planning and producing brand content, \n we combine professional visual skills, a rich creator network, and reliable execution to make every outstanding moment last forever.",
            },
            signature: {
                name: "**Sicheng Zhou**",
                roleEn: "*Head of Creative Content & Creator Partnerships*",
                roleZh: "创意内容与品牌合作负责人",
                brand: "**HEI CREATIVE | 巨能創意**",
            },
        },
        contact: {
            title: "Contact",
            emailLabel: "Email:",
            formIntro: "Or you can fill in this form and I will contact you",
            firstName: "First Name",
            lastName: "Last Name",
            email: "Email",
            date: "When would you like to take pictures?",
            message: "Please leave a message",
            submitted: "Submitted! I will contact you shortly!",
        },
        portfolio: {
            clickToExplore: "Click to explore",
            portrait: "Portrait",
            wedding: "Wedding",
            event: "Event",
            commercial: "Exhibitions",
            pets: "Pets",
            fashion: "Fashion",
            destination: "Destination",
            creative: "Creative",
            scenery: "Scenery",
        },
        creatorPartnerships: {
            heroEyebrow: "Influencer & Creator Network",
            heroIntro: "We work with a curated network of influential Key Opinion Leaders and content creators based in the UK, reaching diverse content verticals and core audience communities. Our team has delivered commercial content campaigns across multiple brands and categories.",
            heroBody: "Backed by a rich creator network and proven brand experience, we help brands match the right content partners and build authentic, compelling brand storytelling across the UK and China markets.",
            sections: [
                {
                    title: "Creator Partnerships",
                    body: "We have signed a broad range of influential Key Opinion Leader and premium content creators based in the UK, precisely covering diverse content fields and core audience communities. Across multiple brands and categories, we have successfully delivered commercial content collaborations.",
                    image: "https://files.seeusercontent.com/2026/08/31/Bs3d/DSC00293-5-2.jpg",
                    alt: "Key Opinion Leader Partnerships",
                },
                {
                    title: "Offline Brand Events",
                    body: "Invite quality Key Opinion Leaders to deeply engage with brand launches, pop-up experiences, and large-scale offline events, strengthening immersive on-site interaction and driving viral social sharing.",
                    image: "https://files.seeusercontent.com/2026/08/31/kx7I/23-D3emhE2b.jpg",
                    alt: "Offline Brand Events",
                },
                {
                    title: "Online Brand Promotion",
                    body: "Match top creators based on brand positioning, and amplify brand awareness through in-depth product experiences, premium content creation, and multi-platform social distribution.",
                    image: "https://files.seeusercontent.com/2026/08/31/hm8V/Creator-Content.png",
                    alt: "Online Brand Promotion",
                },
                {
                    title: "Diverse Collaboration Formats",
                    body: "Explore rich content collaboration formats, including street interviews, in-store and factory visits, and live-streaming interaction, to craft customised brand communication journeys.",
                    image: "https://files.seeusercontent.com/2026/08/31/ml0M/33-Bh5l7g1V.jpg",
                    alt: "Diverse Collaboration Formats",
                },
            ],
        },
    },
    delivery: {
        meta: {
            invalidLink: "Invalid Link",
            expiredLink: "Link Expired",
            gallery: "Photo Gallery",
            consent: "Informed Consent",
        },
        consent: {
            agree: "Continue",
            leave: "Leave",
            checkbox: "I have read and understood the informed consent above",
            checkRequired: "Please read the informed consent",
            title: "Photo Delivery · Informed Consent",
            social: {
                title: "When sharing the photos on social media, feel free to tag me — thank you for being in front of the camera!",
                copy: "Copy UserName",
                copied: "Copied",
                copyFailed: "Copy failed — please copy manually",
            },
            clauses: {
                thanks: "Thank you for choosing HEI CREATIVE. Please read the following terms before viewing your photos.",
                sponsorIntro:
                    "These event photos were shot and sponsored free of charge by photographer HEI CREATIVE. Thank you for joining — feel free to forward this album to all participants.",
                expiry: "This link has an expiry date. Please watch the countdown and finish viewing, selecting, and downloading before it expires.",
                commercial:
                    "Commercial use, resale, or sublicensing without written permission is not permitted.",
                copyright: "Copyright of the photos remains with the photographer.",
                colorGrading:
                    "The photos have only been lightly color-graded. For further retouching or any post-processing questions, feel free to contact the photographer.",
                shooting:
                    "If you have any shooting needs, you are welcome to reach me through the social media below — I look forward to working with you.",
                socialLabel: "Show social media cards",
            },
        },
        expired: {
            title: "Link Expired",
            body: "This link has expired. Please contact the photographer for a new link.",
            archiveBody: "Your photos have been archived. Archive storage details:",
        },
        error: {
            title: "Invalid Link",
            subtitle: "Could not parse this delivery link. Please check the URL or contact the photographer for a new link.",
        },
        countdown: {
            remaining: "Remaining",
            day: "d",
            hour: "h",
            minute: "m",
            second: "s",
        },
        actions: {
            pinned: "Pinned",
            download: "Download",
            downloadAll: "Download ZIP",
            downloadAllPhotos: "Download all",
            downloadAllMobileTitle: "Best downloaded on a computer",
            downloadAllMobileBody:
                "The full album is a large file and may fail or use a lot of memory on mobile. Please open this link on a computer to download all photos.",
            downloadAllMobileContinue: "Download anyway",
            downloadAllFailed: "Could not start the download. Please contact the photographer.",
            downloadSelected: "Download selected",
            downloadSelectedPhotos: "Download photos",
            select: "Select",
            selectAll: "Select all",
            selectAllPhotos: "Select photos",
            selectFolderAll: "Download folder",
            selectedCount: "Selected",
            selectedFilesUnit: "files",
            selectedPhotosUnit: "photos",
            selectedFoldersUnit: "folders",
            cancel: "Cancel",
            close: "Close",
            previous: "Previous",
            next: "Next",
            preview: "Preview",
            folderActions: "Folder actions",
            backToParent: "Parent folder",
            backToParentTitle: "Back to parent folder",
            downloading: "Packaging",
            downloadingPhotos: "Downloading",
            downloadConfirm: "Pack",
            downloadConfirmSuffix: "files (incl. subfolders) into a ZIP",
            downloadHint:
                "Uses streaming ZIP for desktop batch download. Failed files are retried and listed in a report inside the ZIP.",
            zipPartialSuccess:
                "ZIP download started. {failed} file(s) failed — see _download-failures.txt in the archive.",
            mobileZipSuggestTitle: "Recommended on desktop",
            mobileZipSuggestBody:
                "Batch or folder downloads may use a lot of memory on mobile. Copy the link below and open it in a desktop browser.",
            mobileZipContinue: "Still download ZIP on mobile",
            copyLink: "Copy link",
            linkCopied: "Link copied",
            linkCopyFailed: "Copy failed — please copy manually",
            photosDownloadStarted: "Individual downloads started",
            mobileSaveHint: 'Long-press the image above, then choose "Add to Photos"',
            mobileBatchSaveTitle: "Save photos one by one",
            mobileBatchSaveIntro: "Scroll down and long-press each photo to save",
            viewMode: "View mode",
            viewGrid: "Browse",
            viewWaterfall: "Masonry",
            albumLoadFailed: "Could not load this album. Please contact the photographer.",
            albumNotFound: "Album not found. Please check the link or contact the photographer.",
            contactPhotographer: "Contact photographer",
            retry: "Retry",
            emptyFolder: "This folder is empty",
            downloadFailed: "Download failed",
            noFilesToDownload: "No files to download",
            countFilesFailed: "Could not count files",
            scanningFiles: "Scanning files",
            packingProgress: "Packing {current}/{total}{failedHint}…",
            failedHint: " ({count} failed)",
            zipDownloadStarted: "ZIP download started",
            zipDownloadFailed: "ZIP download failed",
            countingFiles: "Counting files…",
            loadingText: "Loading text…",
            textTooLarge: "File too large (over {size}KB). Please download to view.",
            textLoadFailed: "Could not load text content",
            previewUrlFailed: "Could not get preview URL",
            loadingAlbum: "Loading album…",
            loadMore: "Load more",
            defaultAlbumName: "Album",
            missingApiHint:
                "The photographer must deploy the Cloudflare Worker and set REACT_APP_DELIVERY_API_URL.",
            selectionFoldersPart: ", {folders} {unit}",
        },
        embed: {
            fileLoadFailed: "Failed to load file. Please check your network and try again.",
            loading: "Loading…",
        },
        api: {
            missingApi: "Delivery API is not configured. Please contact the photographer.",
        },
        downloadErrors: {
            cancelled: "Download cancelled",
            noDownloadUrl: "Could not get download URL",
            httpFailed: "Download failed (HTTP {status})",
            missingUrl: "Missing download URL",
            downloadFailed: "Download failed",
            noFiles: "No files to download",
            apiNotConfigured: "Delivery API is not configured",
            streamingUnsupported:
                "Streaming download is not supported in this browser. Please use Chrome, Edge, or desktop Safari.",
            failureReportTitle: "Failed downloads",
            failureReportAlbum: "Album: {name}",
            failureReportGenerated: "Generated: {time}",
            failureReportCount: "{count} file(s)",
            failureReportName: "Name: {name}",
            failureReportKey: "Key: {key}",
            failureReportError: "Error: {error}",
            failureReportReason: "Reason: {reason}",
            failureReportDomain: "Domain: {domain}",
            failureReportIndex: "Index: {index}/{total}",
            failureReportAttempts: "Attempts: {attempts}",
            failureReportBody: "Body: {body}",
        },
        admin: {
            title: "Delivery",
            subtitle: "Generate delivery link",
            description:
                "Folder delivery is served by a Cloudflare Worker reading from R2; credentials are not exposed in the frontend.",
            albumNameLabel: "Album name",
            albumNamePlaceholder: "Zhang San Wedding",
            albumNameHint: "Shown on the QR poster and share page. Required when generating a link.",
            albumNameRequired: "Please enter the album name",
            pathLabel: "R2 delivery path",
            pathPlaceholder: "wedding-zhang-san",
            pathHint:
                "After running upload-delivery-to-r2.js, paste the path printed by the script here.",
            pathRequired: "Please enter the R2 delivery path",
            pathInvalid: "Invalid path format. Check for .., backslashes, or empty segments.",
            expiryLabel: "Expiry",
            archiveMarkdownLabel: "Archive storage details (Markdown, optional)",
            archiveMarkdownPlaceholder:
                "e.g.\nYour photos are archived here:\n[Open archive](https://example.com/archive)",
            archiveMarkdownHint:
                "You can leave this blank and add it later by editing the link. Markdown text and links are supported and will be shown after expiry.",
            consentLabel: "Consent notice",
            consentHint:
                "Tick the clauses to show when the client opens the link. Leave all unticked to skip consent and go straight to the album.",
            storageModeLabel: "Link type",
            storageShort: "Short link (D1)",
            storageInline: "Self-contained (base64)",
            storageShortHint:
                "Stores the details in D1; the link is just a random short ID. Manageable and editable later.",
            storageInlineHint:
                "Legacy mode: all details are base64-encoded into the link. Not stored, so it cannot be managed or revoked — it only expires.",
            downloadAllLabel: "Download all photos",
            downloadAllHint:
                "Optional. Adds a Download all button next to Select. Use an R2 archive (e.g. all.zip; no slash means inside the album folder) or a Google Drive file ID.",
            downloadAllNone: "Off",
            downloadAllZip: "R2 archive",
            downloadAllGdrive: "Google Drive",
            downloadAllZipPlaceholder: "all.zip",
            downloadAllGdrivePlaceholder: "Google Drive file ID",
            downloadAllValueRequired: "Please enter the archive name or Google Drive file ID",
            generate: "Generate delivery link",
            resultTitle: "Delivery URL",
            expiresAt: "Expires at {time} (your local time)",
            viewCount: "Views: {count}",
            copyLink: "Copy link",
            linkCopied: "Link copied",
            copyFailed: "Copy failed — please copy manually",
            expiryMustBeFuture: "Expiry must be later than the current time",
            generateFailed: "Generation failed. Please check your input.",
            posterTitle: "QR poster",
            posterHint: "Download or share this poster so clients can scan the code to open the album.",
            manageTitle: "Manage delivery links",
            manageSubtitle: "Only short links (stored in D1) appear here. You can edit every field except the ID.",
            manageEmpty: "No delivery links yet.",
            manageLoadFailed: "Could not load delivery links",
            expiredTag: "Expired",
            validTag: "Active",
            edit: "Edit",
            getPoster: "Get poster",
            editTitle: "Edit delivery link",
            save: "Save",
            editDone: "Saved",
            editFailed: "Save failed",
            deleteConfirm: "Delete this delivery link?",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            deleteDone: "Deleted",
            deleteFailed: "Delete failed",
        },
        share: {
            pageTitle: "Share Album",
            shareButton: "Share",
            subtitle: "分享相册",
            subtitleEn: "Share album",
            copyLink: "Copy link",
            linkCopied: "Link copied",
            copyFailed: "Copy failed — please copy manually",
            downloadPoster: "Download QR poster",
            posterDownloaded: "Poster downloaded",
            posterDownloadFailed: "Poster download failed",
            posterLoading: "Generating poster…",
            posterFailed: "Could not generate poster",
            posterAlt: "QR poster for {name}",
            urlLabel: "Album link",
            backToGallery: "Back to gallery",
            poster: {
                subtitle: "Photo Delivery",
                scanHint: "Scan to download your photos",
                expiryLabel: "Album Available Until",
            },
        },
    },
    selection: {
        meta: {
            invalidLink: "Invalid Link",
            expiredLink: "Link Expired",
            gallery: "Photo Selection",
            consent: "Selection Notice",
        },
        api: {
            missingApi: "Selection API is not configured. Please contact the photographer.",
        },
        consent: {
            agree: "Got it, start selecting",
            leave: "Leave",
            checkbox: "I have read and understood the informed consent above",
            checkRequired: "Please read the informed consent",
            title: "Photo Selection · Informed Consent",
            social: {
                title: "When sharing the photos on social media, feel free to tag me — thank you for being in front of the camera!",
                copy: "Copy UserName",
                copied: "Copied",
                copyFailed: "Copy failed — please copy manually",
            },
            clauses: {
                thanks: "Welcome to HEI CREATIVE online photo selection. Please read the following terms before selecting your photos.",
                sponsorIntro:
                    "These event photos were shot and sponsored free of charge by photographer HEI CREATIVE. Thank you for joining — feel free to forward this selection link to all participants.",
                expiry: "This link has an expiry date. Please finish selecting and submitting your photos before it expires.",
                commercial:
                    "Commercial use, resale, or sublicensing without written permission is not permitted.",
                copyright: "Copyright of the photos remains with the photographer.",
                colorGrading:
                    "The photos have only been lightly color-graded. For further retouching or any post-processing questions, feel free to contact the photographer.",
                selectionQuality:
                    "These photos have only received preliminary colour grading; they are not final retouched images and do not represent the quality of the final delivered images.",
                shooting:
                    "If you have any shooting needs, you are welcome to reach me through the social media below — I look forward to working with you.",
                socialLabel: "Show social media cards",
            },
            variants: {
                general: {
                    title: "Selection Notice",
                    paragraphs: [
                        "Welcome to HEI CREATIVE online photo selection. Please enter your name, then start picking the photos you like.",
                        "1. While browsing, tap the “+” on the top-right of a photo to add it to your selection, then open the selection cart and tap “Submit” — don't forget to submit, otherwise the photographer won't receive your selection.",
                        "2. Your selection is saved on this device automatically. Entering the same selector name again will auto-load the photos you previously chose, so you can keep adjusting before submitting.",
                        "3. This link has an expiry date. Please complete and submit your selection in time.",
                    ],
                },
                sponsor: {
                    title: "Event Selection Notice",
                    paragraphs: [
                        "Hello! These photos were shot by photographer HEI CREATIVE. Please enter your name and pick the photos you would like retouched or kept.",
                        "1. While browsing, tap the “+” on the top-right of a photo to add it to your selection, then open the selection cart and tap “Submit” — don't forget to submit, otherwise the photographer won't receive your selection.",
                        "2. Your selection is saved on this device automatically. Entering the same name again will auto-load the photos you previously chose, so you can keep adjusting before submitting.",
                        "3. This link has an expiry date. For any questions, feel free to contact the photographer.",
                    ],
                },
            },
        },
        name: {
            title: "Enter your name",
            subtitle: "Your name is used only to identify this selection so the photographer knows who submitted it.",
            placeholder: "Enter your name",
            required: "Please enter your name",
            start: "Start selecting",
            back: "Back",
        },
        gallery: {
            defaultAlbumName: "Album",
            loadingAlbum: "Loading album…",
            albumLoadFailed: "Could not load this album. Please contact the photographer.",
            albumNotFound: "Album not found. Please check the link or contact the photographer.",
            missingApiHint:
                "The photographer must deploy the Cloudflare Worker and set REACT_APP_DELIVERY_API_URL.",
            contactPhotographer: "Contact photographer",
            retry: "Retry",
            emptyFolder: "This folder is empty",
            loadMore: "Load more",
            backToParent: "Parent folder",
            backToParentTitle: "Back to parent folder",
            download: "Download",
            downloading: "Downloading",
            downloadFailed: "Download failed",
            close: "Close",
            previous: "Previous",
            next: "Next",
            addPick: "Add to selection",
            removePick: "Remove from selection",
            pinned: "Pinned",
            viewCart: "Selected",
            downloadAllPhotos: "Download all photos",
            goToCart: "Review & submit",
            selectedCount: "{count} photo(s) selected",
            restored: "Loaded {count} photo(s) from your previous selection",
            noteLabel: "Retouch notes (optional)",
            notePlaceholder:
                "Describe how you'd like this photo retouched, e.g. smooth skin, brighten tones, remove people in the background…",
            noteHint: "Adding retouch notes will automatically add this photo to your selection.",
            noteSave: "Save notes",
            noteSaved: "Retouch notes saved",
            noteSubmitReminder: "Don't forget to submit your selection!",
            saveFailed:
                "Local storage isn't available, so this change wasn't saved. It may be lost on refresh or exit — please submit your selection soon.",
        },
        cart: {
            title: "My Selection",
            summary: "{name} · {count} selected",
            anonymous: "Selector",
            continue: "Keep selecting",
            empty: "No photos selected yet",
            goSelect: "Go select",
            remove: "Remove",
            clear: "Clear",
            clearConfirm: "Clear all selected photos?",
            clearOk: "Clear",
            clearCancel: "Cancel",
            submit: "Submit selection",
            submitHint:
                "After submitting, the photographer receives your selection. You can keep adding or removing photos before submitting.",
            submitDone: "Selection submitted",
            submitFailed: "Submit failed. Please try again.",
            expired: "This link has expired and cannot be submitted.",
            needName: "Please enter your name first",
            successTitle: "Selection submitted",
            successSubtitle:
                "Thank you {name}! You submitted {count} photo(s). The photographer will process them soon.",
            backToGallery: "Back to album",
            note: "Retouch notes",
            noteAdd: "Add retouch notes",
            noteEditTitle: "Retouch notes",
            notePlaceholder:
                "Describe how you'd like this photo retouched, e.g. smooth skin, brighten tones, remove people in the background…",
            noteSave: "Save",
            noteCancel: "Cancel",
            noteSaved: "Retouch notes saved",
        },
        admin: {
            title: "Selection",
            subtitle: "Generate a selection link",
            description:
                "Selection link details are stored in D1; submitted results are visible on the Results page.",
            albumNameLabel: "Album name",
            albumNamePlaceholder: "Zhang San Wedding",
            albumNameHint: "Shown on the selection page. Required when generating a link.",
            albumNameRequired: "Please enter the album name",
            pathLabel: "R2 album path",
            pathPlaceholder: "wedding-zhang-san",
            pathHint: "Same as delivery: after uploading to R2, paste the path printed by the script here.",
            expiryLabel: "Expiry",
            archiveMarkdownLabel: "Archive storage details (Markdown, optional)",
            archiveMarkdownPlaceholder:
                "e.g.\nYour photos are archived here:\n[Open archive](https://example.com/archive)",
            archiveMarkdownHint:
                "Optional. You can add it later by editing the link; Markdown text and links will be shown after the selection link expires.",
            consentLabel: "Consent notice",
            consentHint:
                "Tick the clauses to show when the client opens the selection link. Leave all unticked to skip consent and go straight to selection.",
            downloadAllLabel: "Download all photos",
            downloadAllHint:
                "Optional. After configuring this, a “Download all photos” button appears beside the selection cart. Enter an R2 ZIP object (for example all.zip; without a slash it defaults to the album folder) or a Google Drive file ID.",
            downloadAllNone: "Disabled",
            downloadAllZip: "R2 ZIP",
            downloadAllGdrive: "Google Drive",
            downloadAllZipPlaceholder: "all.zip",
            downloadAllGdrivePlaceholder: "Google Drive file ID",
            downloadAllValueRequired: "Please enter the ZIP filename or Google Drive file ID",
            consentGeneral: "General",
            consentSponsor: "Event",
            generate: "Generate selection link",
            generateFailed: "Generation failed. Please check your input.",
            resultTitle: "Selection URL",
            expiresAt: "Expires at {time} (your local time)",
            viewCount: "Views: {count}",
            copyLink: "Copy link",
            linkCopied: "Link copied",
            copyFailed: "Copy failed — please copy manually",
            expiryMustBeFuture: "Expiry must be later than the current time",
            posterTitle: "QR poster",
            posterHint: "Download or share this poster so clients can scan the code to open the selection album.",
            manageTitle: "Manage selection links",
            manageSubtitle: "Manage generated selection links. You can edit every field except the ID.",
            manageEmpty: "No selection links yet.",
            manageLoadFailed: "Could not load selection links",
            viewResults: "View results",
            expiredTag: "Expired",
            validTag: "Active",
            edit: "Edit",
            getPoster: "Get poster",
            editTitle: "Edit selection link",
            save: "Save",
            editDone: "Saved",
            editFailed: "Save failed",
            deleteConfirm: "Delete this selection link?",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            deleteDone: "Deleted",
            deleteFailed: "Delete failed",
        },
        share: {
            subtitle: "选片相册",
            subtitleEn: "Photo selection",
            copyLink: "Copy link",
            linkCopied: "Link copied",
            copyFailed: "Copy failed — please copy manually",
            downloadPoster: "Download QR poster",
            posterDownloaded: "Poster downloaded",
            posterDownloadFailed: "Poster download failed",
            posterLoading: "Generating poster…",
            posterFailed: "Could not generate poster",
            posterAlt: "Selection QR poster for {name}",
            urlLabel: "Selection link",
            poster: {
                subtitle: "Photo Selection",
                scanHint: "Scan to start selecting your photos",
                expiryLabel: "Album Available Until",
            },
        },
        results: {
            title: "Selection Results",
            subtitle: "View client selections grouped by album, with search, date filters, and pagination. Details load on demand.",
            loadFailed: "Could not load selection results",
            detailLoadFailed: "Could not load selection details",
            detailLoading: "Loading selection details…",
            refresh: "Refresh",
            search: "Search",
            resetFilters: "Reset",
            searchPlaceholder: "Search by selector, album name, or path",
            dateFrom: "Start date",
            dateTo: "End date",
            empty: "No selection results yet.",
            emptyFiltered: "No selection results match your filters.",
            unknownAlbum: "Uncategorized album",
            albumSubmissionCount: "{count} submissions",
            pageRange: "Items {from}–{to}",
            hasMoreSuffix: " (more may exist)",
            photoCount: "{count} photos",
            submittedAt: "Submitted {time}",
            view: "View",
            deleteConfirm: "Delete this selection record?",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            deleteDone: "Deleted",
            deleteFailed: "Delete failed",
            detailTitle: "{name}'s selection",
            albumLabel: "Album",
            copyPaths: "Copy photo paths",
            pathsCopied: "Photo paths copied",
            copyFailed: "Copy failed — please copy manually",
            close: "Close",
            note: "Retouch notes",
            copyNotes: "Copy retouch notes",
            notesCopied: "Retouch notes copied",
            noNotes: "No retouch notes in this selection",
            exportCsv: "Export CSV",
            exportDone: "CSV exported",
            exportFailed: "Export failed",
            csvIndex: "No.",
            csvName: "File name",
            csvPath: "Path",
            csvNote: "Retouch notes",
        },
    },
    videoReview: {
        api: {
            missingApi: "Video review API is not configured. Set REACT_APP_DELIVERY_API_URL.",
        },
        consent: {
            agree: "Continue",
            leave: "Leave",
            checkbox: "I have read and understood the informed consent above",
            checkRequired: "Please read the informed consent",
            title: "Video Delivery · Informed Consent",
            social: {
                title: "When sharing the video on social media, feel free to tag me — thank you!",
                copy: "Copy username",
                copied: "Copied",
                copyFailed: "Copy failed — please copy manually",
            },
            clauses: {
                thanks: "Thank you for choosing HEI CREATIVE. Please read the following terms before watching the video and leaving your feedback.",
                sponsorIntro:
                    "This event video was shot and sponsored free of charge by photographer HEI CREATIVE. Feel free to share this link with all participants.",
                expiry: "This link has an expiry date. Please finish watching and reviewing before it expires.",
                commercial:
                    "Commercial use, resale, or sublicensing of the video without written permission is not permitted.",
                copyright: "Copyright of the video belongs to the photographer.",
                colorGrading:
                    "The video has only received preliminary colour grading and editing. For further changes, contact the photographer or leave timestamped comments.",
                shooting: "If you need videography services, feel free to reach out via the social links below.",
                socialLabel: "Show social media cards",
            },
        },
        name: {
            title: "Enter your name",
            subtitle: "Your name is only used to identify your comments.",
            placeholder: "Your name",
            required: "Please enter your name",
            start: "Start review",
            back: "Back",
        },
        gallery: {
            defaultVideoName: "Video",
            notFound: "Link not found. Please check the URL or contact the photographer.",
            loadFailed: "Could not load this video review link. Please try again.",
            videoMissing: "Video file not found. Please contact the photographer.",
        },
        download: {
            button: "Download video",
            downloading: "Preparing download",
            started: "Download started",
            failed: "Download failed. Please try again.",
            confirmTitle: "Download this video?",
            confirmBody: "This video file is large. It is recommended not to download on mobile.",
            confirmOk: "Download anyway",
        },
        comments: {
            title: "Comments",
            empty: "No comments yet. Pause the video and add feedback at the current time.",
            placeholder: "Describe what should be changed at this moment…",
            submit: "Post comment",
            posted: "Comment posted",
            postFailed: "Could not post comment",
            loadFailed: "Could not load comments",
            expired: "Link expired — comments are closed",
            atTime: "Comment at {time}",
            individualHint: "Only your comments",
            delete: "Delete comment",
            deleteConfirm: "Delete this comment?",
            deleted: "Comment deleted",
            deleteFailed: "Could not delete comment",
        },
        admin: {
            title: "Video review",
            subtitle: "Create a video review link",
            description:
                "Share a single video with clients. They can pause and leave timestamped comments for editing feedback.",
            manageTitle: "Manage video reviews",
            manageSubtitle: "Edit, copy, or delete video review links.",
            manageEmpty: "No video review links yet",
            manageLoadFailed: "Could not load video review links",
            searchPlaceholder: "Search by video title",
            pageRange: "{from}–{to} of {total}",
            viewResults: "View comments",
            videoNameLabel: "Video title",
            videoNamePlaceholder: "Zhang San — Wedding film v1",
            videoNameHint: "Shown on the review page.",
            videoNameRequired: "Please enter a video title",
            pathLabel: "R2 video path",
            pathPlaceholder: "client/wedding/final.mp4",
            pathHint: "Single video file key in R2 (upload manually first).",
            pathRequired: "Please enter the video path",
            pathInvalid: "Invalid video path",
            consentHint:
                "Choose which clauses clients see before reviewing. Leave all unchecked to skip consent and go straight to the review page.",
            expiryLabel: "Link expiry",
            expiryMustBeFuture: "Expiry must be in the future",
            consentLabel: "Consent type",
            consentGeneral: "General",
            consentSponsor: "Sponsor",
            visibilityLabel: "Comment visibility",
            visibilityShared: "Everyone sees all",
            visibilityIndividual: "Each person sees own",
            visibilitySharedHint: "All reviewers can read every comment on this link.",
            visibilityIndividualHint: "Each reviewer only sees their own comments. You see all in admin.",
            generate: "Create link",
            generateFailed: "Could not create link",
            resultTitle: "Link created",
            expiresAt: "Expires {time}",
            copyLink: "Copy link",
            linkCopied: "Link copied",
            copyFailed: "Could not copy",
            edit: "Edit",
            editTitle: "Edit video review link",
            editDone: "Saved",
            editFailed: "Could not save",
            save: "Save",
            deleteConfirm: "Delete this link and all comments?",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            deleteDone: "Deleted",
            deleteFailed: "Could not delete",
            expiredTag: "Expired",
            validTag: "Active",
            viewCount: "Views: {count}",
            commentCount: "Comments: {count}",
            posterTitle: "QR poster",
            posterHint: "Download or share this poster so clients can scan the code to open the video review page.",
            getPoster: "Get poster",
        },
        share: {
            subtitle: "视频评价",
            subtitleEn: "Video review",
            copyLink: "Copy link",
            linkCopied: "Link copied",
            copyFailed: "Copy failed — please copy manually",
            downloadPoster: "Download QR poster",
            posterDownloaded: "Poster downloaded",
            posterDownloadFailed: "Poster download failed",
            posterLoading: "Generating poster…",
            posterFailed: "Could not generate poster",
            posterAlt: "Video review QR poster for {name}",
            urlLabel: "Video review link",
            poster: {
                subtitle: "Video Review",
                scanHint: "Scan to watch & leave timestamped feedback",
                expiryLabel: "Review Link Available Until",
            },
        },
        audit: {
            badge: "Admin review",
            reviewButton: "Review comments",
            backToManage: "Back to manage",
            allCommentsHint: "All comments visible",
        },
        results: {
            title: "Video review comments",
            subtitle: "Browse timestamped feedback and export subtitles for editing.",
            selectLink: "Select video link",
            loadFailed: "Could not load comments",
            noComments: "No comments for this link",
            copyAll: "Copy all",
            allCopied: "Comments copied",
            timestampCopied: "Timestamp copied",
            exportCsv: "Export CSV",
            exportSrt: "Export SRT",
            exportVtt: "Export VTT",
            exportSubtitle: "Export subtitle",
            exportDone: "File downloaded",
            exportFailed: "Export failed",
            colTimestamp: "Timestamp",
            colReviewer: "Reviewer",
            colBody: "Comment",
            colSubmittedAt: "Submitted at",
            colActions: "Actions",
        },
    },
    retouchReview: {
        admin: {
            title: "Retouch Review",
            manageTitle: "Retouch Review Manage",
        },
    },
    fileTransfer: {
        admin: {
            title: "File Transfer",
            manageTitle: "File Transfer Manage",
            fieldTitle: "Title *",
            fieldTitlePlaceholder: "e.g. Project files, design assets",
            fieldTitleHint: "Give this transfer session a name",
            fieldDescription: "Description",
            fieldDescriptionPlaceholder: "Optional: add instructions visible to link recipients",
            fieldExpiry: "Expiry",
            day: "1 day",
            days: "{days} days",
            fieldMaxFileSize: "Max file size",
            bytes: "bytes",
            maxFileSizeHint: "Current: {size}, max 5 TB",
            fieldMaxFiles: "Max file count",
            maxFilesHint: "Max 10,000 files",
            createButton: "Create transfer link",
            resultTitle: "Transfer link created",
            resultDesc: "Anyone with this link can upload and download files",
            copyLink: "Copy link",
            preview: "Preview",
            successCreated: "Transfer session created",
            subtitle: "Create a file transfer session that generates a single link for both upload and download",
            createFailed: "Create failed",
            linkCopied: "Link copied",
            copyFailed: "Copy failed",
        },
        manage: {
            title: "File Transfer Manage",
            subtitle: "Manage all file transfer sessions",
            noSessions: "No transfer sessions",
            edit: "Edit",
            editTitle: "Edit transfer session",
            save: "Save",
            cancel: "Cancel",
            saved: "Updated",
            saveFailed: "Save failed",
            deleteConfirmTitle: "Delete this transfer session?",
            deleteConfirmDesc: "The following will be permanently deleted: {count} file(s) ({size}) and all transfer records.",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            deleteDone: "Session deleted{extra}",
            deleteFailed: "Delete failed",
            expired: "Expired",
            loadFailed: "Load failed",
            noTitle: "(No title)",
            copied: "Link copied",
        },
        session: {
            title: "File Transfer",
            expired: "Link expired",
            expiredDesc: "{title} has expired",
            backHome: "Back to home",
            loading: "Loading...",
            loadFailed: "Failed to load",
            timeLeft: "{time} remaining",
            limits: "Max {size} per file, up to {count} files",
            uploadSection: "Upload files",
            uploadText: "Click or drag files here to upload",
            uploadHint: "Any format, max {size} per file",
            uploadSuccess: "{name} uploaded",
            uploadFailed: "{name} upload failed: {msg}",
            uploadRetryHint: "Please re-select \"{name}\"",
            networkError: "Network error",
            partFailed: "Part upload failed: {msg}",
            fileSection: "Uploaded files",
            noFiles: "No files uploaded yet",
            downloadAll: "Download all as ZIP",
            downloadAllDone: "Download complete",
            downloadAllFailed: "Download failed: {msg}",
            downloadAllWarn: "No files to download",
            downloadAllUnsupported: "Your browser does not support ZIP batch download. Please use the single-file download buttons below.",
            downloadAllPartial: "ZIP complete: {succeeded} succeeded, {failed} failed. The failure list is included in the ZIP.",
            downloadStart: "Starting download, check your browser's download manager",
            downloadFailed: "Download failed: {msg}",
            streamFailed: "Streaming download failed",
            fileNotReady: "File not ready",
            deleteConfirm: "Confirm delete",
            deleteConfirmDesc: "Delete {name}? This action cannot be undone.",
            deleteDone: "{name} deleted",
            deleteFailed: "Delete failed: {msg}",
            zipProgress: "Downloading: {current}/{total}",
            zipProgressFailed: ", {count} failed",
            zipDone: "Download complete",
            cancelled: "Cancelled",
        },
        form: {
            titleRequired: "Please enter a title",
            titleTooLong: "Title too long",
            descTooLong: "Description too long",
            expiryRequired: "Please set an expiry time",
            expiryMustBeFuture: "Expiry must be in the future",
            maxFileSizeInvalid: "Invalid max file size (1B – 5TB)",
            maxFilesInvalid: "Invalid max file count (1 – 10000)",
        },
    },

    feedback: {
        prompt: "Have feedback?",
        link: "Click here",
        api: {
            missingApi: "Feedback API is not configured. Set REACT_APP_DELIVERY_API_URL.",
        },
        modal: {
            title: "Submit feedback",
            contentPlaceholder: "Tell us what’s on your mind…",
            contactPlaceholder: "Contact info (optional, email or phone)",
            contentRequired: "Please enter your feedback",
            submit: "Submit",
            cancel: "Cancel",
            success: "Thank you for your feedback!",
            error: "Could not submit feedback. Please try again.",
        },
        admin: {
            title: "User feedback",
            subtitle: "Feedback submitted from delivery and selection album pages.",
            searchPlaceholder: "Search content, album, contact, or IP",
            dateFrom: "From",
            dateTo: "To",
            search: "Search",
            resetFilters: "Reset filters",
            refresh: "Refresh",
            empty: "No feedback yet",
            emptyFiltered: "No feedback matches your filters",
            sourceDelivery: "Delivery",
            sourceSelection: "Selection",
            submittedAt: "Submitted {time}",
            view: "View",
            detailTitle: "Feedback detail",
            deleteConfirm: "Delete this feedback?",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            deleted: "Deleted",
            deleteFailed: "Delete failed",
            loadFailed: "Could not load feedback",
            exportCsv: "Export CSV",
            exportDone: "CSV exported",
            exportFailed: "Export failed",
            pageRange: "{from}–{to}",
            hasMoreSuffix: " (more may exist)",
            colTime: "Time",
            colSource: "Source",
            colAlbum: "Album",
            colLinkId: "Link ID",
            colContent: "Content",
            colContact: "Contact",
            colIp: "IP",
            colActions: "Actions",
            unknownAlbum: "Unknown album",
            allSources: "All sources",
            albumPath: "Album path",
        },
    },
    quote: {
        page: {
            title: "Quotation",
            subtitle: "Fill in line items and generate a shareable quotation image.",
            preview: "Preview quotation",
            reset: "Reset form",
            resetDone: "Form reset",
        },
        form: {
            basicInfo: "Basic information",
            lineItems: "Line items",
            projectName: "Client / project name",
            projectNamePlaceholder: "Zhang San Wedding",
            quoteDate: "Quote date",
            validUntil: "Valid until",
            currency: "Currency",
            portfolioUrl: "Portfolio link",
            portfolioUrlPlaceholder: "https://heicreative.co.uk",
            globalNotes: "General notes",
            globalNotesHint: "Each note appears on its own line at the bottom of the quotation.",
            globalNotePlaceholder: "Enter a note",
            addNote: "Add note",
            removeNote: "Remove",
            itemName: "Item",
            itemNamePlaceholder: "Wedding coverage",
            unitPrice: "Unit price",
            quantity: "Qty",
            note: "Note",
            notePlaceholder: "e.g. Includes 50 edited photos",
            subtotal: "Subtotal",
            addLine: "Add line",
            removeLine: "Remove line",
            grandTotal: "Grand total",
            hideSubitemPrices: "Hide all line-item prices",
        },
        categories: {
            photography: "Photography & video",
            equipment: "Equipment",
            travel: "Travel",
            other: "Other",
        },
        validation: {
            projectNameRequired: "Please enter a client or project name",
            quoteDateRequired: "Please select a quote date",
            portfolioUrlInvalid: "Invalid portfolio URL. Use an http or https link.",
        },
        preview: {
            downloadPoster: "Download quotation image",
            openPoster: "Generate quotation image",
            posterDownloaded: "Quotation downloaded",
            posterDownloadFailed: "Could not generate quotation",
            posterLoading: "Generating quotation…",
            posterFailed: "Could not generate quotation",
            imageTabTitle: "Quotation",
            saveSheetHint: "Choose Save Image in the system share sheet to save it to Photos.",
            saveToPhotos: "Save to Photos",
            openImage: "Open image",
            longPressHint: "You can also long-press the image to save it",
            closePreview: "Close",
        },
        poster: {
            title: "报价单",
            titleEn: "QUOTATION",
            projectPlaceholder: "Project name",
            validUntil: "Valid until",
            colItem: "Item",
            colCalc: "Unit × Qty",
            colAmount: "Amount",
            sectionSubtotal: "Subtotal",
            grandTotal: "TOTAL",
            scanZh: "扫码查看样片",
            scanEn: "Scan for portfolio",
            socialRed: "小红书 @伦敦摄影师小黑",
            socialIg: "Instagram @Mr.Hei_",
        },
    },
    booking: {
        panel: {
            title: "Dashboard",
            subtitle: "Signed in.",
            signOut: "Sign out",
        },
        admin: {
            title: "Bookings",
            subtitle: "Create a booking, generate a QR poster, and let clients scan to view the details.",
            new: "New booking",
            empty: "No bookings yet. Tap New booking to add one.",
            poster: "Poster",
            posterTitle: "Booking poster",
            copyLink: "Copy link",
            createdAt: "Created",
            loadFailed: "Failed to load bookings",
            createDone: "Booking created",
            createFailed: "Create failed, please retry",
            linkCopied: "Link copied",
            deleteConfirm: "Delete this booking?",
            deleteOk: "Delete",
            deleteCancel: "Cancel",
            deleteDone: "Deleted",
            deleteFailed: "Delete failed",
            searchPlaceholder: "Search name, location, content, or ID",
            dateFrom: "Shoot from",
            dateTo: "Shoot to",
            search: "Search",
            resetFilters: "Reset filters",
            refresh: "Refresh",
            emptyFiltered: "No bookings match your filters",
            pageRange: "{from}–{to}",
            hasMoreSuffix: " (more may exist)",
        },
        form: {
            title: "Booking name",
            titlePlaceholder: "e.g. Mr. Zhang Wedding",
            titleRequired: "Please enter a booking name",
            location: "Location",
            locationPlaceholder: "e.g. St. Paul's, London",
            locationRequired: "Please enter the location",
            shootAt: "Shoot time",
            shootAtPlaceholder: "Pick date and time",
            shootAtRequired: "Please select the shoot time",
            shootAtLocalHint: "Enter the local time at the shoot location.",
            shootAtHour: "Hour",
            shootAtMinute: "Minute",
            duration: "Duration",
            durationPlaceholder: "Optional, e.g. 2 hours / full day",
            content: "Shoot details",
            contentPlaceholder: "Describe the shoot, party size, special requests, etc.",
            contentRequired: "Please enter the shoot details",
            notes: "Notes",
            notesPlaceholder: "Optional, e.g. dress code, parking info",
            submit: "Create & generate poster",
        },
        poster: {
            title: "预约确认",
            titleEn: "BOOKING",
            titlePlaceholder: "Booking name",
            time: "Time",
            localTime: "(The shooting time is local time)",
            duration: "Duration",
            location: "Place",
            content: "Details",
            notes: "Notes",
            scanZh: "扫码查看预约详情",
            scanEn: "Scan to view booking",
            downloadPoster: "Download poster",
            openPoster: "Generate poster",
            downloaded: "Poster downloaded",
            downloadFailed: "Could not download poster",
            loading: "Generating poster…",
            failed: "Could not generate poster",
            longPressHint: "Long-press the image to save it",
            closePreview: "Close",
        },
        view: {
            loading: "Loading",
            notFoundTitle: "Booking not found or expired",
            notFoundText: "Please check the link or contact your photographer.",
            networkErrorTitle: "Couldn't load this booking",
            networkErrorText:
                "There seems to be a network issue, so the booking details couldn't be loaded.",
            troubleshootHint:
                "Please try clearing the cache, switching networks, or opening the link in your default browser.",
            contactPrompt: "Still not working?",
            contactLink: "Contact me",
            debugToggle: "Technical details",
        },
    },
};

export default en;
